import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { importBomRouting, validateBomRoutingImport } from "../api/materials";

const HEADERS = {
  op_no: ["opr no", "op no", "operasyon no", "no"],
  description: ["aciklama", "açıklama", "operasyon"],
  work_center_group: ["makine grubu", "is merkezi grubu", "iş merkezi grubu", "grup"],
  output_material_code: ["cikan malzeme kodu", "çıkan malzeme kodu", "output", "cikan", "çıkan"],
  input_material_code: ["giren malzeme kodu", "giren", "input"],
  setup_min: ["setup", "hazirlik", "hazırlık"],
  machine_min: ["makine", "machine"],
  labor_min: ["iscilik", "işçilik", "labor"],
};

function normalizeHeaderKey(x) {
  return String(x || "")
    .trim()
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/\s+/g, " ");
}

function findHeaderIndex(headers, aliases) {
  const norm = headers.map(normalizeHeaderKey);
  for (const a of aliases) {
    const idx = norm.indexOf(normalizeHeaderKey(a));
    if (idx >= 0) return idx;
  }
  return -1;
}

export default function BomRoutingImportModal({ onClose, onImported }) {
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState("");
  const [rows, setRows] = useState([]);

  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(null);

  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  const errorCount = useMemo(() => (validated?.errors || []).length, [validated]);
  const canSubmit = !!rows.length && !validating && !saving && errorCount === 0 && !!validated;

  async function handlePickFile(file) {
    setParseError("");
    setSubmitError("");
    setResult(null);
    setRows([]);
    setValidated(null);
    setFileName(file?.name || "");
    if (!file) return;

    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      const sheetName = wb.SheetNames?.[0];
      if (!sheetName) throw new Error("Excel sayfası bulunamadı");
      const ws = wb.Sheets[sheetName];

      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      if (!Array.isArray(aoa) || aoa.length < 2) {
        throw new Error("Excel boş veya başlık satırı eksik");
      }

      const headerRow = (aoa[0] || []).map((x) => String(x || "").trim());
      const idx = {
        op_no: findHeaderIndex(headerRow, HEADERS.op_no),
        description: findHeaderIndex(headerRow, HEADERS.description),
        work_center_group: findHeaderIndex(headerRow, HEADERS.work_center_group),
        output_material_code: findHeaderIndex(headerRow, HEADERS.output_material_code),
        input_material_code: findHeaderIndex(headerRow, HEADERS.input_material_code),
        setup_min: findHeaderIndex(headerRow, HEADERS.setup_min),
        machine_min: findHeaderIndex(headerRow, HEADERS.machine_min),
        labor_min: findHeaderIndex(headerRow, HEADERS.labor_min),
      };

      const required = ["op_no", "description", "work_center_group", "output_material_code", "input_material_code"];
      const missing = required.filter((k) => idx[k] < 0);
      if (missing.length) {
        throw new Error(`Eksik zorunlu sütun(lar): ${missing.join(", ")}`);
      }

      const parsed = [];
      for (let i = 1; i < aoa.length; i += 1) {
        const r = aoa[i] || [];
        const get = (k) => (idx[k] >= 0 ? String(r[idx[k]] ?? "").trim() : "");
        const rec = {
          op_no: get("op_no"),
          description: get("description"),
          work_center_group: get("work_center_group"),
          output_material_code: get("output_material_code"),
          input_material_code: get("input_material_code"),
          setup_min: get("setup_min"),
          machine_min: get("machine_min"),
          labor_min: get("labor_min"),
        };
        // Tamamen boş satırı atla
        if (Object.values(rec).every((v) => !v)) continue;
        parsed.push(rec);
      }

      if (!parsed.length) {
        setParseError("Dosyada veri satırı bulunamadı.");
        return;
      }

      setRows(parsed);

      setValidating(true);
      try {
        const res = await validateBomRoutingImport(parsed);
        setValidated(res);
      } catch (err) {
        console.error("BOM/ROUTING VALIDATE ERROR", err);
        setParseError(err?.response?.data?.error?.message || err?.message || "Doğrulama başarısız");
      } finally {
        setValidating(false);
      }
    } catch (err) {
      console.error("EXCEL PARSE ERROR", err);
      setParseError(err?.message || "Excel okunamadı");
    }
  }

  async function submit() {
    setSubmitError("");
    setResult(null);
    if (!rows.length) return;
    if (errorCount > 0) {
      setSubmitError("Hatalı satırlar var. Lütfen düzeltin.");
      return;
    }

    setSaving(true);
    try {
      const res = await importBomRouting(rows);
      setResult(res);
      await onImported?.(res);
    } catch (err) {
      console.error("BOM/ROUTING IMPORT ERROR", err);
      const status = err?.response?.status;
      const dataObj = err?.response?.data;
      const message =
        dataObj?.error?.message ||
        dataObj?.message ||
        (typeof dataObj === "string" ? dataObj : null) ||
        err?.message ||
        null;
      setSubmitError(`İçe aktarılamadı${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
    } finally {
      setSaving(false);
    }
  }

  const opErrorMap = useMemo(() => {
    const m = new Map();
    for (const e of validated?.errors || []) {
      m.set(Number(e.op_no), e.errors);
    }
    return m;
  }, [validated]);

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          BOM ve Rota Excel İçe Aktar
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Sütunlar: <b>Opr No</b>, <b>Açıklama</b>, <b>Makine Grubu</b>, <b>Çıkan Malzeme Kodu</b>, <b>Giren Malzeme Kodu</b>, opsiyonel <b>Setup/Makine/İşçilik</b>.
          Aynı operasyonun ek giren malzemeleri için boş hücre bırakılabilir (forward-fill). Top-level rota en yüksek Opr No'lu satırın çıkan malzemesine bağlanır.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {parseError ? <Alert severity="error">{parseError}</Alert> : null}
          {submitError ? <Alert severity="error">{submitError}</Alert> : null}

          {result ? (
            <Alert severity={result?.errors?.length ? "warning" : "success"}>
              Top-level malzeme ID: <b>{result.top_level_material_id}</b>
              <br />
              Routing oluşturuldu (v{result.routing_version}, ID {result.routing_id}) — operasyon: <b>{result.operations_inserted}</b>
              <br />
              BOM: <b>{result.boms_created}</b> ürün için yeni reçete oluşturuldu
            </Alert>
          ) : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <Button variant="outlined" component="label" disabled={saving}>
              Dosya Seç (.xlsx)
              <input
                type="file"
                hidden
                accept=".xlsx,.xls,.csv"
                onChange={(e) => handlePickFile(e.target.files?.[0] || null)}
              />
            </Button>
            <Typography variant="body2" color="text.secondary">
              {fileName ? `Seçili: ${fileName}` : "Henüz dosya seçilmedi"}
              {validating ? " (doğrulanıyor...)" : ""}
            </Typography>
          </Stack>

          {validated?.operations?.length ? (
            <>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Top-level malzeme: <b>{validated.top_level_material_code || "—"}</b> — Operasyon: <b>{validated.operations.length}</b> — Hata: <b>{errorCount}</b>
                </Typography>
              </Box>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Op No</TableCell>
                      <TableCell>Açıklama</TableCell>
                      <TableCell>Makine Grubu</TableCell>
                      <TableCell>Çıkan</TableCell>
                      <TableCell>Giren Malzemeler</TableCell>
                      <TableCell>Setup / Makine / İşçilik</TableCell>
                      <TableCell>Durum</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(validated.operations || []).map((op) => {
                      const errs = opErrorMap.get(Number(op.op_no));
                      return (
                        <TableRow key={op.op_no} hover>
                          <TableCell>
                            <b>{op.op_no}</b>
                          </TableCell>
                          <TableCell>{op.description || "—"}</TableCell>
                          <TableCell>{op.work_center_group || "—"}</TableCell>
                          <TableCell>{op.output_material_code || "—"}</TableCell>
                          <TableCell sx={{ maxWidth: 320 }}>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {(op.inputs || []).map((c, i) => (
                                <Chip key={i} size="small" label={c} variant="outlined" />
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {op.setup_min || 0} / {op.machine_min || 0} / {op.labor_min || 0}
                          </TableCell>
                          <TableCell>
                            {errs && errs.length ? (
                              <Typography variant="caption" color="error">
                                {errs.join(", ")}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="success.main">
                                OK
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Kapat
        </Button>
        <Button onClick={submit} variant="contained" disabled={!canSubmit}>
          {saving ? "Oluşturuluyor..." : "Oluştur"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
